import React, { useCallback, useEffect, useState } from 'react';
import { Eye, EyeOff, Info } from 'lucide-react';
import api from 'config/axiosConfig';
import { useUser } from 'util/UserContext';
import InputField from 'ui/InputField';
import { UsernameValidationResponse } from 'util/constants';
import AuthService from 'services/authService';
import RESTRICTED_WORD_LIST from 'config/restrictedWordList';
import VerticalSeparator from 'ui/VerticalSeparator';
import { setExistingAccountCookie } from 'util/existingUserCookie';
import { registerSchema, signinSchema } from 'lib/validation/authSchemas';
import { getApiErrorMessage } from 'lib/errors/normalizeApiError';
import { apiRoutes } from "util/apiRoutes";
import { getAuthUiStrings, resolveAuthLocale } from "i18n/authUi";

/**
 * Stable id used to pair the signin form with the parent AuthModal's
 * footer submit button. The parent passes the same id to both `Modal`
 * (so screen-reader and devtools association is correct) and to its
 * `FormActions`, which renders `<button form={LOCAL_SIGNIN_FORM_ID}
 * type="submit">`. This is what enables browser-native Enter-key submit.
 *
 * See `docs/architecture/forms-standard.md` section 2.
 */
export const LOCAL_SIGNIN_FORM_ID = "auth-local-signin-form";

interface LocalSigninProps {
  onClose: () => void;
  onForgotPassword: (email: string) => void;
  onUseCode: () => void;
  onRegistrationSuccess: (email: string) => void;
  initialEmail?: string;
  initialIsRegistering?: boolean;
  onRegisteringChange?: (value: boolean) => void;
  onStateChange?: (state: {
    isSubmitting: boolean;
    isRegistering: boolean;
    isButtonDisabled: boolean;
  }) => void;
}

const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  maxLength: 100,
};

const USERNAME_REQUIREMENTS = {
  minLength: 2,
  maxLength: 30,
};

const RESTRICTED_USERNAME_LIST = RESTRICTED_WORD_LIST;

const LocalSignin: React.FC<LocalSigninProps> = ({
  onClose,
  onForgotPassword,
  onUseCode,
  onRegistrationSuccess,
  initialEmail = "",
  initialIsRegistering = false,
  onRegisteringChange,
  onStateChange,
}) => {
  const { refreshUser } = useUser();
  const authUi = getAuthUiStrings(resolveAuthLocale());
  const [isRegistering, setIsRegistering] = useState(initialIsRegistering);
  const [email, setEmail] = useState(initialEmail);
  const [emailOrUsername, setEmailOrUsername] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
  const [isValidUsername, setIsValidUsername] = useState<boolean>(false);
  const [validationMessage, setValidationMessage] = useState<string>('');
  const [isPrivate, setIsPrivate] = useState<boolean>(false);
  const [hasInteractedPassword, setHasInteractedPassword] = useState<boolean>(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);

  // PKCE state
  const [codeVerifier, setCodeVerifier] = useState<string>('');
  const [codeChallenge, setCodeChallenge] = useState<string>('');

  // Generate PKCE parameters on mount or when isRegistering changes.
  useEffect(() => {
    if (onRegisteringChange) {
      onRegisteringChange(isRegistering);
    }

    const generateRandomString = (length: number): string => {
      const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
      let result = '';
      const values = new Uint8Array(length);
      window.crypto.getRandomValues(values);
      for (let i = 0; i < length; i++) {
        result += charset[values[i] % charset.length];
      }
      return result;
    };

    const generateCodeChallenge = async (verifier: string): Promise<string> => {
      const encoder = new TextEncoder();
      const data = encoder.encode(verifier);
      const digest = await window.crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(digest));
      const base64String = btoa(String.fromCharCode(...hashArray))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
      return base64String;
    };

    const initPKCE = async () => {
      const verifier = generateRandomString(64);
      const challenge = await generateCodeChallenge(verifier);
      setCodeVerifier(verifier);
      setCodeChallenge(challenge);
    };

    initPKCE();
  }, [isRegistering, onRegisteringChange]);

  useEffect(() => {
    if (initialEmail) {
      setEmail(initialEmail);
      setEmailOrUsername(initialEmail);
      setIsEmailValid(true);
    }
  }, [initialEmail]);

  const validateUsername = async (usernameToCheck: string): Promise<void> => {
    setIsValidUsername(true);
    setValidationMessage('');
    if (
      /^[a-z0-9_.]{2,30}$/.test(usernameToCheck) &&
      !/\.{2,}/.test(usernameToCheck) &&
      !usernameToCheck.startsWith(".") &&
      !usernameToCheck.startsWith("_") &&
      !usernameToCheck.endsWith(".") &&
      !usernameToCheck.endsWith("_") &&
      !usernameToCheck.includes("..") &&
      !usernameToCheck.includes("__") &&
      !/^admin/.test(usernameToCheck) &&
      !/admin$/.test(usernameToCheck) &&
      !RESTRICTED_USERNAME_LIST.includes(usernameToCheck)
    ) {
      try {
        const response = await api.get<UsernameValidationResponse>(apiRoutes.user.byUsername(usernameToCheck));
        if (response.status === 404) {
          setIsValidUsername(true);
          setValidationMessage('');
        } else {
          setIsValidUsername(false);
          setValidationMessage(authUi.usernameTaken);
        }
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 404) {
          setIsValidUsername(true);
          setValidationMessage('');
        } else {
          setIsValidUsername(false);
          setValidationMessage(authUi.usernameTaken);
        }
      }
    } else {
      if (
        usernameToCheck.startsWith(".") ||
        usernameToCheck.startsWith("_") ||
        usernameToCheck.endsWith(".") ||
        usernameToCheck.endsWith("_")
      ) {
        setIsValidUsername(false);
        setValidationMessage(authUi.usernameStartEndChar);
      } else if (
        /^admin/.test(usernameToCheck) ||
        /admin$/.test(usernameToCheck) ||
        RESTRICTED_USERNAME_LIST.includes(usernameToCheck)
      ) {
        setIsValidUsername(false);
        setValidationMessage(authUi.usernameNotAllowed);
      } else if (!/^[a-z0-9_.]{2,30}$/.test(usernameToCheck)) {
        setIsValidUsername(false);
        setValidationMessage(authUi.usernameInvalidChars);
      } else if (usernameToCheck.includes("..") || usernameToCheck.includes("__")) {
        setIsValidUsername(false);
        setValidationMessage(authUi.usernameConsecutiveChar);
      }
    }
  };

  useEffect(() => {
    if (username && username.length > 1) {
      validateUsername(username);
    } else if (username && (username.startsWith(".") || username.startsWith("_"))) {
      setIsValidUsername(false);
      setValidationMessage(authUi.usernameStartEndChar);
    } else if (username && username.length < USERNAME_REQUIREMENTS.minLength) {
      setIsValidUsername(false);
      setValidationMessage(authUi.usernameTooShort);
    } else {
      setValidationMessage('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setUsername(e.target.value.toLowerCase());
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = e.target.value;
    setEmail(emailValue);
    setIsEmailValid(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailValue));
  };

  const handleEmailOrUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmailOrUsername(value);
  };

  /**
   * Submit handler wired to the form's `onSubmit`. Uses
   * `e.preventDefault()` so the browser-native Enter-key submit (via
   * the modal-footer `<button form={...} type="submit">` pair) does not
   * navigate away.
   */
  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    setError('');
    setIsSubmitting(true);
    try {
      if (isRegistering) {
        const registerValidation = registerSchema.safeParse({ email, password, username, isPrivate });
        if (!registerValidation.success) {
          setError(registerValidation.error.issues[0]?.message || authUi.authFailedFallback);
          setIsSubmitting(false);
          return;
        }
        const response = await api.post(apiRoutes.auth.register, {
          email,
          password,
          username,
          isPrivate,
          code_verifier: codeVerifier,
          code_challenge: codeChallenge
        });
        if (response.status === 201) {
          if (response.data.accessToken) {
            AuthService.setAccessToken(response.data.accessToken);
          }
          try {
            let accountId: number | null = null;
            let uname: string | null = null;
            const data: unknown = response.data;
            if (typeof (data as { id?: number })?.id === 'number') {
              accountId = (data as { id: number }).id;
            } else if (typeof (data as { user?: { id?: number } })?.user?.id === 'number') {
              accountId = (data as { user: { id: number } }).user.id;
            }
            if (typeof (data as { username?: string })?.username === 'string') {
              uname = (data as { username: string }).username;
            } else if (typeof (data as { user?: { username?: string } })?.user?.username === 'string') {
              uname = (data as { user: { username: string } }).user.username;
            }
            if (!uname && username) {
              uname = username;
            }
            if (accountId === null || !uname) {
              const userResp = await api.get(apiRoutes.user.root);
              const userData = userResp.data as { id: number | string; username?: string };
              const parsedId = typeof userData.id === 'string' ? parseInt(userData.id, 10) : userData.id;
              if (typeof parsedId === 'number') {
                accountId = parsedId;
              }
              if (!uname && typeof userData.username === 'string') {
                uname = userData.username;
              }
            }
            if (accountId !== null && typeof uname === 'string' && uname.length > 0) {
              setExistingAccountCookie(accountId, uname);
            }
          } catch (cookieErr) {
            // Best-effort: failure to write the existing-account cookie must not block signup.
          }
          onRegistrationSuccess(email);
        }
      } else {
        const signinValidation = signinSchema.safeParse({ emailOrUsername, password });
        if (!signinValidation.success) {
          setError(signinValidation.error.issues[0]?.message || authUi.authFailedFallback);
          setIsSubmitting(false);
          return;
        }
        const response = await api.post(apiRoutes.auth.signin, {
          emailOrUsername,
          password,
          code_verifier: codeVerifier,
          code_challenge: codeChallenge
        });
        if (response.status === 200) {
          if (response.data.accessToken) {
            AuthService.setAccessToken(response.data.accessToken);
          }
          await refreshUser();
          onClose();
        }
      }
    } catch (err) {
      // Prefers backend response.data.message over axios's technical
      // "Request failed with status code N" default. See
      // `lib/errors/normalizeApiError`.
      setError(getApiErrorMessage(err, authUi.authFailedFallback));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isRegistering,
    password,
    codeVerifier,
    codeChallenge,
    email,
    username,
    isPrivate,
    refreshUser,
    onClose,
    onRegistrationSuccess,
    emailOrUsername,
    authUi.authFailedFallback,
  ]);

  // Sign-up gates the button on the registration password policy
  // (8 char min, visible to the user via the counter so they know how
  // close they are). Sign-in only requires SOMETHING to submit -- the
  // backend is the sole authority on credential validity, and gating
  // the button on the policy would leak the minimum length to
  // attackers AND lock out anyone whose existing credential predates
  // a future policy tightening.
  const isPasswordReadyForSubmit = isRegistering
    ? password.length >= PASSWORD_REQUIREMENTS.minLength
    : password.length > 0;
  const isButtonDisabled =
    ((isRegistering && (!username || !isValidUsername)) ||
     (isRegistering && (!email || !isEmailValid)) ||
     (!isRegistering && emailOrUsername.length < 2) ||
     isSubmitting ||
     !isPasswordReadyForSubmit);

  useEffect(() => {
    if (onStateChange) {
      onStateChange({ isSubmitting, isRegistering, isButtonDisabled });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, isRegistering, isButtonDisabled]);

  return (
    <div className="flex flex-col flex-grow space-y-4">
      <VerticalSeparator text="OR" />

      <form
        id={LOCAL_SIGNIN_FORM_ID}
        onSubmit={handleSubmit}
        className="flex flex-col flex-grow space-y-4"
        noValidate
      >
        <div className="form-control mb-0">
          {isRegistering ? (
            <InputField
              type="email"
              id="email"
              name="email"
              labelText={authUi.emailLabel}
              autoComplete="email"
              fieldRequired
              value={emailOrUsername.includes('@') ? emailOrUsername : email}
              onChange={handleEmailChange}
              validationState={isEmailValid ? 'valid' : email ? 'invalid' : 'none'}
              spellCheck={false}
            />
          ) : (
            <InputField
              type="email"
              id="emailOrUsername"
              name="emailOrUsername"
              labelText={authUi.emailOrUsernameLabel}
              autoComplete="username"
              fieldRequired
              value={emailOrUsername}
              onChange={handleEmailOrUsernameChange}
              validationState={emailOrUsername.length >= 2 ? 'valid' : emailOrUsername.length > 0 ? 'invalid' : 'none'}
              spellCheck={false}
            />
          )}
        </div>

        <div className="form-control">
          {/*
            Password policy attributes (`maxLength`/`minLength`,
            character counter, valid/invalid validation icon) are
            applied ONLY on sign-up. On sign-in we deliberately do
            not surface the registration policy: the visible counter,
            HTML5 minLength tooltip, and validation icon would all
            telegraph the minimum length to an attacker -- and would
            also lock out users whose existing credential predates
            any future policy change. The backend is the sole
            authority on whether a credential is valid and returns a
            generic "invalid credentials" error.
          */}
          <InputField
            type={isPasswordVisible ? "text" : "password"}
            id="password"
            name="password"
            labelText={authUi.passwordLabel}
            autoComplete={isRegistering ? 'new-password' : 'current-password'}
            fieldRequired
            value={password}
            onChange={(e) => { setPassword(e.target.value); setHasInteractedPassword(true); }}
            onBlur={() => setHasInteractedPassword(true)}
            validationState={
              isRegistering
                ? (password.length === 0 && !hasInteractedPassword
                    ? 'none'
                    : (password.length >= PASSWORD_REQUIREMENTS.minLength && password.length <= PASSWORD_REQUIREMENTS.maxLength)
                      ? 'valid'
                      : 'invalid')
                : 'none'
            }
            spellCheck={false}
            maxLength={isRegistering ? PASSWORD_REQUIREMENTS.maxLength : undefined}
            minLength={isRegistering ? PASSWORD_REQUIREMENTS.minLength : undefined}
            trailingAction={{
              primaryIcon: Eye,
              secondaryIcon: EyeOff,
              isPrimaryActive: !isPasswordVisible,
              onToggle: () => setIsPasswordVisible((v) => !v),
              primaryLabel: authUi.showPasswordLabel,
              secondaryLabel: authUi.hidePasswordLabel,
            }}
          />
        </div>
        {!isRegistering && (
          <>
            <button
              type="button"
              onClick={() => onForgotPassword(emailOrUsername)}
              className="pl-1 text-sm external-link"
            >
              {authUi.forgotPasswordLink}
            </button>
            <button
              type="button"
              onClick={() => onUseCode()}
              className="pl-1 text-sm external-link"
            >
              {authUi.signInWithCodeLink}
            </button>
          </>
        )}

        {isRegistering && (
          <div className="form-control">
            <InputField
              type="email"
              id="username"
              name="username"
              labelText={authUi.usernameLabel}
              fieldRequired
              value={username}
              onChange={handleUsernameChange}
              validationState={isValidUsername && !error ? 'valid' : username ? 'invalid' : 'none'}
              autoComplete="username"
              error={validationMessage}
              spellCheck={false}
              maxLength={USERNAME_REQUIREMENTS.maxLength}
              minLength={USERNAME_REQUIREMENTS.minLength}
            />
          </div>
        )}

        {isRegistering && (
          <div className="flex items-center gap-2">
            <label htmlFor="privateAccount" className="text-sm pl-1">
              {authUi.privateAccountLabel}
            </label>
            <input
              type="checkbox"
              id="privateAccount"
              checked={isPrivate}
              onChange={(e) => {
                setIsPrivate(e.target.checked);
              }}
              className="toggle-switch-input col-span-1"
            />
          </div>
        )}
        <div className="local-signin-flow-toggle pl-1 flex items-center gap-2 text-sm text-eoe-text-heading dark:text-eoe-dark-text-heading">
          {isRegistering ? (
            <>
              <span>{authUi.returningUserPrompt}</span>
              <button
                type="button"
                onClick={() => {
                  setIsRegistering(false);
                  if (onRegisteringChange) {
                    onRegisteringChange(false);
                  }
                }}
                className="local-signin-flow-toggle-btn external-link"
              >
                {authUi.signInCta}
              </button>
            </>
          ) : (
            <>
              <span>{authUi.newUserPrompt}</span>
              <button
                type="button"
                onClick={() => {
                  setIsRegistering(true);
                  if (onRegisteringChange) {
                    onRegisteringChange(true);
                  }
                  if (emailOrUsername.includes('@')) {
                    handleEmailChange({ target: { value: emailOrUsername } } as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
                className="local-signin-flow-toggle-btn external-link"
              >
                {authUi.signUpCta}
              </button>
            </>
          )}
        </div>

        {error && (
          <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm" role="alert">
            <Info className="mr-1 size-4" />
            <span>{error}</span>
          </div>
        )}
      </form>

      {/* Submit button rendered in AuthModal footer via FormActions (form="auth-local-signin-form"). */}
    </div>
  );
};

export default LocalSignin;
